import CommonService from './common.service';
import b2bHubHomeService from './b2bHubHome.service';
import { fetchPublishedInfrastructureListings } from './infrastructureLeasingListing.service';
import { API_URLS } from '../constants/api-urls';
import { buildQueryCompletions } from '../utils/globalHeaderSearchQueryCompletions';

const SUGGESTION_LIMIT = 8;
const CACHE_TTL_MS = 60_000;
const MIN_QUERY_LENGTH = 1;

/** @type {Map<string, { expires: number, items: object[] }>} */
const responseCache = new Map();

/** @type {Map<string, Promise<object[]>>} */
const inflight = new Map();

function cacheKey(moduleId, query) {
  return `${moduleId}:${String(query || '').trim().toLowerCase()}`;
}

function readCache(key) {
  const row = responseCache.get(key);
  if (!row) return null;
  if (Date.now() > row.expires) {
    responseCache.delete(key);
    return null;
  }
  return row.items;
}

function writeCache(key, items) {
  responseCache.set(key, { expires: Date.now() + CACHE_TTL_MS, items });
}

function collectTexts(values) {
  return values.flatMap((value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    return value ? [String(value)] : [];
  });
}

async function suggestProducts(query, signal) {
  const [res, catRes] = await Promise.all([
    b2bHubHomeService.getMarketplace({
      q: query.trim(),
      limit: 20,
      page: 1,
      sort: 'newest',
    }),
    CommonService.get('/master/master-categories', {}, { silent: true, signal }),
  ]);
  const products = res?.data?.products || [];
  const categories = Array.isArray(catRes?.data) ? catRes.data : [];
  const texts = collectTexts([
    ...products.flatMap((p) => [p.title, p.name, p.category_name, p.company_name]),
    ...categories.flatMap((c) => [c.category, c.name, c.category_name]),
  ]);
  return buildQueryCompletions(query, texts, SUGGESTION_LIMIT);
}

let auctionCache = null;
let auctionCacheAt = 0;

async function loadAuctionTexts(signal) {
  if (auctionCache && Date.now() - auctionCacheAt < CACHE_TTL_MS) {
    return auctionCache;
  }
  const [response, catRes] = await Promise.all([
    CommonService.get(API_URLS.AUCTION.ACTIVE, {}, { silent: true, signal }),
    CommonService.get(`${API_URLS.AUCTION.BASE}/categories`, {}, { silent: true, signal }),
  ]);
  const rows = Array.isArray(response?.data) ? response.data : [];
  const categories = Array.isArray(catRes?.data) ? catRes.data : [];
  let subCategoryRows = [];
  if (categories.length) {
    const subResponses = await Promise.all(
      categories.map((cat) =>
        CommonService.get(
          `${API_URLS.AUCTION.BASE}/categories/${cat.category_id}/sub-categories`,
          {},
          { silent: true, signal },
        ).catch(() => ({ data: [] })),
      ),
    );
    subCategoryRows = subResponses.flatMap((res) => (Array.isArray(res?.data) ? res.data : []));
  }
  auctionCache = collectTexts([
    ...rows.flatMap((a) => [
      a.product_name,
      a.title,
      a.category_name,
      a.sub_category_name,
      a.company_name,
    ]),
    ...categories.flatMap((c) => [c.category_name, c.category_description]),
    ...subCategoryRows.flatMap((sub) => [sub.sub_category_name, sub.sub_category_description]),
  ]);
  auctionCacheAt = Date.now();
  return auctionCache;
}

async function suggestAuctions(query, signal) {
  const texts = await loadAuctionTexts(signal);
  return buildQueryCompletions(query, texts, SUGGESTION_LIMIT);
}

let cmCache = null;
let cmCacheAt = 0;

async function loadContractManufacturerTexts(signal) {
  if (cmCache && Date.now() - cmCacheAt < CACHE_TTL_MS) {
    return cmCache;
  }
  const res = await CommonService.get(
    API_URLS.CONTRACT_MANUFACTURER.LIST,
    { page: 1, limit: 200 },
    { silent: true, signal },
  );
  const items = Array.isArray(res?.data?.items) ? res.data.items : Array.isArray(res?.data) ? res.data : [];
  cmCache = collectTexts(
    items.flatMap((row) => [
      row.company_name,
      row.facility_name,
      row.specialization,
      row.location_label,
    ]),
  );
  cmCacheAt = Date.now();
  return cmCache;
}

async function suggestContractManufacturers(query, signal) {
  const texts = await loadContractManufacturerTexts(signal);
  return buildQueryCompletions(query, texts, SUGGESTION_LIMIT);
}

let logisticsCache = null;
let logisticsCacheAt = 0;

async function loadLogisticsTexts(signal) {
  if (logisticsCache && Date.now() - logisticsCacheAt < CACHE_TTL_MS) {
    return logisticsCache;
  }
  const res = await CommonService.get(
    API_URLS.LOGISTICS_PROVIDER.LIST,
    { limit: 200, offset: 0 },
    { silent: true, signal },
  );
  const items = Array.isArray(res?.data?.items) ? res.data.items : Array.isArray(res?.data) ? res.data : [];
  logisticsCache = collectTexts(
    items.flatMap((row) => [
      row.company_name,
      row.headquarters_location,
      row.company_type_label,
      row.service_type,
    ]),
  );
  logisticsCacheAt = Date.now();
  return logisticsCache;
}

async function suggestServiceProviders(query, signal) {
  const texts = await loadLogisticsTexts(signal);
  return buildQueryCompletions(query, texts, SUGGESTION_LIMIT);
}

let infraCache = null;
let infraCacheAt = 0;

async function loadInfrastructureTexts(signal) {
  if (infraCache && Date.now() - infraCacheAt < CACHE_TTL_MS) {
    return infraCache;
  }
  const res = await fetchPublishedInfrastructureListings(200);
  const items = Array.isArray(res?.data?.items) ? res.data.items : [];
  infraCache = collectTexts(
    items.flatMap((row) => [row.title, row.locationLine, row.categoryLabel, row.subCategoryLabel]),
  );
  infraCacheAt = Date.now();
  return infraCache;
}

async function suggestInfrastructure(query, signal) {
  const texts = await loadInfrastructureTexts(signal);
  return buildQueryCompletions(query, texts, SUGGESTION_LIMIT);
}

/**
 * @param {import('../config/globalHeaderSearch').SearchModuleId} moduleId
 * @param {string} query
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ id: string, label: string, type: 'query' }[]>}
 */
export async function fetchGlobalSearchSuggestions(moduleId, query, signal) {
  const trimmed = String(query || '').trim();
  if (trimmed.length < MIN_QUERY_LENGTH) return [];

  const key = cacheKey(moduleId, trimmed);
  const cached = readCache(key);
  if (cached) return cached;

  if (inflight.has(key)) {
    return inflight.get(key);
  }

  const task = (async () => {
    let items = [];
    switch (moduleId) {
      case 'products':
        items = await suggestProducts(trimmed, signal);
        break;
      case 'auctions':
        items = await suggestAuctions(trimmed, signal);
        break;
      case 'contractManufacturers':
        items = await suggestContractManufacturers(trimmed, signal);
        break;
      case 'serviceProviders':
        items = await suggestServiceProviders(trimmed, signal);
        break;
      case 'infrastructureLeasing':
        items = await suggestInfrastructure(trimmed, signal);
        break;
      default:
        items = [];
    }
    writeCache(key, items);
    return items;
  })()
    .catch(() => [])
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, task);
  return task;
}

export function clearGlobalSearchSuggestionCache() {
  responseCache.clear();
  inflight.clear();
  auctionCache = null;
  cmCache = null;
  logisticsCache = null;
  infraCache = null;
}
